import {
  getMinimumLevel1ForAudience
} from "../../data/planning/planningRules";

import {
  getActivityTypeById
} from "../../data/planning/activityTypeCatalog";


/**
 * REHBER360 - OTOMATİK PLAN SLOT ÜRETİCİ
 *
 * Görevi:
 *
 * resolver tarafından doğrulanmış
 * hedef → resmî faaliyet ilişkilerini
 *
 * planlama kurallarındaki minimumlara göre
 * ayrı Düzey 1 çalışma slotlarına dönüştürmek.
 *
 * Bu dosya:
 * - faaliyet adı üretmez,
 * - katalog dışı faaliyet kullanmaz,
 * - faaliyet türü uydurmaz,
 * - unresolved hedefi sessizce geçmez.
 */


// =========================================================
// REHBER360 V1 - OTOMATİK DÜZEY 1 TÜR POLİTİKASI
// =========================================================
//
// NOT:
// Bunlar MEB'in belirli bir hedef için zorunlu tuttuğu
// eşleşmeler değildir.
//
// Rehber360'ın otomatik planlama için kullandığı
// düzenlenebilir varsayılan Düzey 1 türleridir.
//
// Kullanılan bütün ID'ler resmî faaliyet türü
// kataloğunda bulunmak zorundadır.
// =========================================================

const AUTO_LEVEL1_ACTIVITY_TYPE_POLICY = {

  /*
   * Otomatik Robotun varsayılan öğrenci politikası:
   *
   * 1. Seminer
   * 2. Yayın Hazırlama
   *
   * Böylece psikolojik danışmanın çok sayıdaki
   * sınıfa tek tek etkinlik uygulaması varsayılan
   * davranış olmaktan çıkar.
   *
   * Sınıf rehberliği faaliyet türleri katalogda
   * kalır ve Manuel Robotta seçilebilir.
   */
  student: [
    "seminar",
    "publication_preparation"
  ],

  parent: [
    "seminar",
    "publication_preparation"
  ],

  teacher: [
    "seminar"
  ]

};


// =========================================================
// GÜVENLİ DÜZEY 1 TÜR POLİTİKASINI ÇÖZ
// =========================================================

const getVerifiedLevel1TypePolicy =
  audience => {

    const policyIds =
      AUTO_LEVEL1_ACTIVITY_TYPE_POLICY[
        audience
      ] || [];


    return policyIds
      .map(activityTypeId =>
        getActivityTypeById(
          activityTypeId
        )
      )
      .filter(
        activityType =>
          activityType &&
          activityType.level === 1
      );
  };


// =========================================================
// OTOMATİK SLOT ÜRET
// =========================================================

export const buildAutoTargetActivitySlots = ({
  resolutionReport,
  planningProfileId =
    "regular_school"
} = {}) => {

  const slots = [];

  const unresolved = [];

  const errors = [];


  const resolutions =
    Array.isArray(
      resolutionReport?.resolutions
    )
      ? resolutionReport.resolutions
      : [];


  if (resolutions.length === 0) {

    return {

      success: false,

      slots: [],

      unresolved: [],

      blockingUnresolved: [],

      recommendedUnresolved: [],

      errors: [
        {
          code:
            "AUTO_RESOLUTION_REPORT_EMPTY",

          message:
            "Otomatik faaliyet çözümleme raporu bulunamadı."
        }
      ],

      counts: {
        total: 0,
        required: 0,
        recommended: 0
      }

    };
  }


  resolutions.forEach(
    resolution => {

      const rule =
        getMinimumLevel1ForAudience(
          planningProfileId,
          resolution.audience
        ) || {
          count: 0,
          enforcement:
            "not_required"
        };


      const slotCount =
        Number(
          rule.count || 0
        );


      const enforcement =
        rule.enforcement ||
        "not_required";


      if (slotCount <= 0) {
        return;
      }


      const resolvedActivities =
        Array.isArray(
          resolution.activities
        )
          ? resolution.activities
              .filter(Boolean)
          : [];


      /*
       * Bir hedef-kitle için en az bir
       * güvenilir resmî faaliyet konusu
       * bulunmadan slot üretilemez.
       */
      if (
        !resolution.resolved ||
        resolvedActivities.length === 0
      ) {

        unresolved.push({

          targetType:
            resolution.targetType,

          targetId:
            resolution.targetId,

          targetLabel:
            resolution.targetLabel,

          audience:
            resolution.audience,

          developmentArea:
            resolution.developmentArea ||
            null,

          enforcement,

          requiredSlotCount:
            slotCount,

          resolutionReason:
            resolution.resolutionReason ||
            null

        });

        return;
      }


      /*
       * Resolver sırası:
       *
       * 1. exact
       * 2. explicit verified
       *
       * Bu nedenle ilk faaliyet,
       * Otomatik Robotun birincil
       * resmî faaliyet konusudur.
       */
      const primaryActivity =
        resolvedActivities[0];


      const activityTypePolicy =
        getVerifiedLevel1TypePolicy(
          resolution.audience
        );


      /*
       * Resmî Düzey 1 tür politikası
       * gereken slot sayısını karşılamıyorsa
       * sessizce eksik plan üretme.
       */
      if (
        activityTypePolicy.length <
        slotCount
      ) {

        errors.push({

          code:
            "AUTO_LEVEL1_TYPE_POLICY_INSUFFICIENT",

          targetType:
            resolution.targetType,

          targetId:
            resolution.targetId,

          targetLabel:
            resolution.targetLabel,

          audience:
            resolution.audience,

          requiredSlotCount:
            slotCount,

          availableTypeCount:
            activityTypePolicy.length,

          message:
            `"${resolution.targetLabel}" / ${resolution.audience} için ${slotCount} Düzey 1 slot gerekiyor ancak otomatik tür politikası yalnızca ${activityTypePolicy.length} güvenli tür içeriyor.`

        });

        return;
      }


      for (
        let slotIndex = 0;
        slotIndex < slotCount;
        slotIndex += 1
      ) {

        const activityType =
          activityTypePolicy[
            slotIndex
          ];


        const slotPrefix =
          enforcement ===
            "recommended"
            ? "recommended"
            : "required";


        slots.push({

          slotId:
            `${slotPrefix}_${slotIndex + 1}`,

          targetType:
            resolution.targetType,

          targetId:
            resolution.targetId,

          targetLabel:
            resolution.targetLabel,

          audience:
            resolution.audience,

          developmentArea:
            resolution.developmentArea ||
            primaryActivity
              .developmentArea ||
            null,

          activityId:
            primaryActivity.id,

          activityOfficialLabel:
            primaryActivity
              .officialLabel,

          activityLevel:
            1,

          activityTypeId:
            activityType.id,

          activityTypeLabel:
            activityType
              .officialLabel,

          activityTypeShortLabel:
            activityType
              .shortLabel ||
            null,

          enforcement,

          resolutionReason:
            resolution.resolutionReason ||
            null,

          order:
            slotIndex

        });

      }

    }
  );


  const blockingUnresolved =
    unresolved.filter(
      item =>
        item.enforcement ===
          "required" ||
        item.enforcement ===
          "conditional_required"
    );


  const recommendedUnresolved =
    unresolved.filter(
      item =>
        item.enforcement ===
          "recommended"
    );


  const requiredSlots =
    slots.filter(
      slot =>
        slot.enforcement ===
          "required" ||
        slot.enforcement ===
          "conditional_required"
    );


  const recommendedSlots =
    slots.filter(
      slot =>
        slot.enforcement ===
          "recommended"
    );


  return {

    success:
      errors.length === 0 &&
      blockingUnresolved.length === 0,

    slots,

    unresolved,

    blockingUnresolved,

    recommendedUnresolved,

    errors,

    counts: {

      total:
        slots.length,

      required:
        requiredSlots.length,

      recommended:
        recommendedSlots.length

    }

  };
};